import { useState } from "react";
import { ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";

interface PersonalizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: PersonalizationData) => void;
}

export interface PersonalizationData {
  name: string;
  organization: string;
  role: string;
  email: string;
  systemSize?: string;
  focusArea?: string;
}

export function PersonalizeModal({ isOpen, onClose, onSubmit }: PersonalizeModalProps) {
  const [formData, setFormData] = useState<PersonalizationData>({
    name: "",
    organization: "",
    role: "",
    email: "",
    systemSize: "",
    focusArea: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  const isFormValid = formData.name && formData.organization && formData.role && formData.email;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle style={{ fontWeight: 700, color: '#18365E' }}>
            Personalize Your Dashboard
          </DialogTitle>
          <DialogDescription style={{ fontWeight: 500 }}>
            Get a customized strategy overview tailored to your organization in under 30 seconds.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" style={{ fontWeight: 600 }}>
              Name <span className="text-[#FF6E4C]">*</span>
            </Label>
            <Input
              id="name"
              placeholder="Your full name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          {/* Organization */}
          <div className="space-y-2">
            <Label htmlFor="organization" style={{ fontWeight: 600 }}>
              Organization <span className="text-[#FF6E4C]">*</span>
            </Label>
            <Input
              id="organization"
              placeholder="Your healthcare organization"
              value={formData.organization}
              onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
              required
            />
          </div>

          {/* Role */}
          <div className="space-y-2">
            <Label htmlFor="role" style={{ fontWeight: 600 }}>
              Role <span className="text-[#FF6E4C]">*</span>
            </Label>
            <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Select your role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Chief Data Officer">Chief Data Officer</SelectItem>
                <SelectItem value="VP of Analytics">VP of Analytics</SelectItem>
                <SelectItem value="Director of Data Strategy">Director of Data Strategy</SelectItem>
                <SelectItem value="IT Leadership">IT Leadership</SelectItem>
                <SelectItem value="Clinical Analytics">Clinical Analytics</SelectItem>
                <SelectItem value="Other Executive">Other Executive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" style={{ fontWeight: 600 }}>
              Email <span className="text-[#FF6E4C]">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="your.email@organization.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          {/* System Size (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="systemSize" style={{ fontWeight: 600 }}>
              System Size <span className="text-[#717182] text-xs">(Optional)</span>
            </Label>
            <Select value={formData.systemSize} onValueChange={(value) => setFormData({ ...formData, systemSize: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Select system size" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Small (1-3 hospitals)">Small (1-3 hospitals)</SelectItem>
                <SelectItem value="Medium (4-10 hospitals)">Medium (4-10 hospitals)</SelectItem>
                <SelectItem value="Large (11+ hospitals)">Large (11+ hospitals)</SelectItem>
                <SelectItem value="Academic Medical Center">Academic Medical Center</SelectItem>
                <SelectItem value="Community Health System">Community Health System</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Focus Area (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="focusArea" style={{ fontWeight: 600 }}>
              Primary Focus Area <span className="text-[#717182] text-xs">(Optional)</span>
            </Label>
            <Select value={formData.focusArea} onValueChange={(value) => setFormData({ ...formData, focusArea: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Select focus area" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Value-based care">Value-based care</SelectItem>
                <SelectItem value="Population health">Population health</SelectItem>
                <SelectItem value="Clinical analytics">Clinical analytics</SelectItem>
                <SelectItem value="Revenue cycle">Revenue cycle</SelectItem>
                <SelectItem value="Data governance">Data governance</SelectItem>
                <SelectItem value="AI & machine learning">AI & machine learning</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={!isFormValid}
            className="w-full bg-[#FF6E4C] hover:bg-[#FF6E4C]/90 text-white border-0 mt-6 group"
            style={{ fontWeight: 600 }}
          >
            Generate My Strategy Overview
            <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
          </Button>

          <p className="text-xs text-[#717182] text-center mt-3" style={{ fontWeight: 500 }}>
            Your information is secure and will only be used to personalize your experience.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
